import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { inject, observer } from 'mobx-react'
import styled from 'styled-components'

import { loginSchema } from '/components/validators/auth'
import { CONSTS, APIURL, PAGEURL } from '/constants'
import { AdminButton } from '/components/theme/admin/AdminButton'
import PageTitle from '/components/common/PageTitle'
import FormikField from '/components/forms/FormikField'
import ActionPageLoader from '/components/loaders/ActionPageLoader'
import PaymentForm from '/components/subscription/PaymentForm'
import {
  VisbilityContainer,
  HeaderPose,
  CommonPose
} from '/components/home/Animations'

import services from '/components/services'

export const SignupInfo = inject('store')(
  observer(() => {
    const router = useRouter()
    const handleSave = async (values, setErrors) => {
      try {
        const userData = {
          first_name: values.first_name,
          last_name: values.last_name,
          site_name: values.site_name,
          site_url: values.site_url,
          email: values.email,
          phone: values.phone,
          password: values.password,
          accept_terms: values.accept_terms
        }
        const res = await services.post_v2(
          APIURL.API_ADMIN_CONNECT_SIGNUP,
          userData
        )
        if (res.statusCode === 200) {
          router.push(PAGEURL.CONNECT_FINISHED)
        } else if (res.statusCode === 500) {
          setErrors({
            global:
              'Unable to submit the form at this time. Please try again later'
          })
        } else {
          setErrors({
            global: res.message
          })
        }
      } catch (e) {
        console.error(e)
      }
    }

    return (
      <VisbilityContainer>
        <Panel>
          <H1>Let&apos;s setup your account</H1>
          <FormWrapper>
            <Formik
              initialValues={{
                first_name: '',
                last_name: '',
                email: '',
                site_name: '',
                site_url: '',
                phone: '',
                password: '',
                accept_terms: false
              }}
              validationSchema={Yup.object({
                first_name: Yup.string().required('Required'),
                last_name: Yup.string().required('Required'),
                email: Yup.string().required('Required'),
                site_name: Yup.string().required('Required'),
                site_url: Yup.string(),
                phone: Yup.string().required('Required'),
                password: Yup.string()
                  .min(8, 'Password is too short - should be 8 chars minimum.')
                  .matches(
                    /[a-z]+/,
                    'Password must contain lower case letters.'
                  )
                  .matches(
                    /[A-Z]+/,
                    'Password must contain upper case letters.'
                  )
                  .matches(/[0-9]+/, 'Password must numbers (i.e. 0-9)')
                  .matches(
                    /[!@#$%^&*]+/,
                    'Password must contain one special character (e.g. !@#$%^&*)'
                  )
                  .required('Required'),
                accept_terms: Yup.boolean()
                  .required('Required')
                  .oneOf([true], 'You must accept the terms and conditions.')
              })}
              onSubmit={async (values, { setSubmitting, setErrors }) => {
                await handleSave(values, setErrors)
                setSubmitting(false)
              }}
            >
              {({ isValid, isSubmitting, errors }) => (
                <Form>
                  <div>
                    {errors.global && (
                      <Row>
                        <Errors
                          dangerouslySetInnerHTML={{ __html: errors.global }}
                        />
                      </Row>
                    )}
                    <Row>
                      <Col>
                        <FormikField name="first_name">
                          <Field
                            className="form-control"
                            name="first_name"
                            type="text"
                            placeholder="First Name"
                            autoFocus
                          />
                        </FormikField>
                      </Col>
                      <Col>
                        <FormikField name="last_name">
                          <Field
                            className="form-control"
                            name="last_name"
                            type="text"
                            placeholder="Last Name"
                          />
                        </FormikField>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <FormikField name="phone">
                          <Field
                            className="form-control"
                            name="phone"
                            type="text"
                            placeholder="Phone Number"
                          />
                        </FormikField>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <FormikField name="email">
                          <Field
                            className="form-control"
                            name="email"
                            type="text"
                            placeholder="Email"
                          />
                        </FormikField>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <FormikField name="password">
                          <Field
                            className="form-control"
                            name="password"
                            type="password"
                            placeholder="Password"
                          />
                        </FormikField>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <FormikField name="site_name">
                          <Field
                            className="form-control"
                            name="site_name"
                            type="text"
                            placeholder="Business Name"
                          />
                        </FormikField>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <FormikField name="site_url">
                          <Field
                            className="form-control"
                            name="site_url"
                            type="text"
                            placeholder="Business Website (Optional)"
                          />
                        </FormikField>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <CheckboxRow>
                          <div>
                            <Field
                              name="accept_terms"
                              id="accept_terms"
                              type="checkbox"
                            />
                          </div>
                          <Label htmlFor="accept_terms">
                            Accept Terms of Service
                          </Label>
                        </CheckboxRow>
                        {errors.accept_terms && (
                          <div className="text-danger">
                            {errors.accept_terms}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                  <Action className="text-center">
                    <AdminButton
                      type="submit"
                      isLoading={isSubmitting}
                      disabled={!isValid}
                    >
                      Let&apos; do this
                    </AdminButton>
                  </Action>
                </Form>
              )}
            </Formik>
          </FormWrapper>
        </Panel>
      </VisbilityContainer>
    )
  })
)

export const Login = inject('store')(
  observer(({ store }) => {
    const { siteData } = store
    const router = useRouter()
    const title = `Chalice Connect - Login`
    return (
      <VisbilityContainer>
        <Panel>
          <PageTitle title={title} />
          <FormWrapper maxWidth="500px">
            <H1>Login</H1>
            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={Yup.object(loginSchema)}
              onSubmit={async (values, { setSubmitting, setErrors }) => {
                setSubmitting(true)
                try {
                  const data = {
                    email: values.email,
                    password: values.password
                  }
                  const res = await services.post_v2(
                    APIURL.API_ADMIN_CONNECT_LOGIN,
                    data
                  )
                  if (res.statusCode === 302) {
                    router.push(res.response.url)
                  } else {
                    setErrors({ email: res.message })
                  }
                } catch (e) {
                  console.error(e)
                  setErrors({
                    email: 'Sorry, we are experiencing issues right now'
                  })
                }
                setSubmitting(false)
              }}
            >
              {({ isSubmitting }) => (
                <Form>
                  <Row>
                    <Col>
                      <Field
                        type="email"
                        name="email"
                        placeholder="Email"
                        className="form-control"
                        autoFocus
                      />
                      <ErrorMessage
                        name="email"
                        component={ErrorMessageWrapper}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        type="password"
                        name="password"
                        placeholder="Password"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="password"
                        component={ErrorMessageWrapper}
                      />
                    </Col>
                  </Row>
                  <RowCenter>
                    <Link href={PAGEURL.FORGOT_PASSWORD}>
                      <a>Don&apos;t remember your password?</a>
                    </Link>
                  </RowCenter>
                  <RowCenter>
                    <AdminButton
                      type="submit"
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                    >
                      Login
                    </AdminButton>
                  </RowCenter>
                </Form>
              )}
            </Formik>
            {siteData.get('allow_signup') && (
              <RowCenter>
                Don&apos;t have an account yet? <br />
                <Link href={PAGEURL.SIGNUP}>
                  <a>Become a Member</a>
                </Link>
              </RowCenter>
            )}
          </FormWrapper>
        </Panel>
      </VisbilityContainer>
    )
  })
)

export const VerifyToken = inject('store')(
  observer(({ store }) => {
    const router = useRouter()
    const [state, setState] = useState({})

    useEffect(() => {
      const signup_token = window.atob(router.query.token)
      const { localStorage } = window
      async function check() {
        try {
          const res = await services.post_v2(APIURL.API_VALIDATE_SIGNUP_TOKEN, {
            signup_token
          })
          if (res.status === 'success') {
            setState({
              auth_email: res.response.email,
              is_valid_signup_token: true
            })
            const { unique_code, login_token, userData } = res.response
            store.login(unique_code, login_token, userData)
            if (store.isAuthenticated()) {
              const next = localStorage.getItem('next')
              if (next) {
                localStorage.removeItem('next')
                router.push(next)
              } else if (
                userData.role_id == CONSTS.SUPER_ADMIN_ROLE ||
                userData.role_id == CONSTS.ENTERPRISE_ADMIN_ROLE
              ) {
                router.push(PAGEURL.ADMIN_DASHBOARD)
              } else {
                router.push(PAGEURL.DASHBOARD)
              }
            }
            return
          }
        } catch (e) {
          console.error(e)
        }
        setState({
          auth_email: null,
          is_valid_signup_token: false
        })
      }
      check()
    }, [])

    let content = null
    if (state.is_valid_signup_token === null) {
      content = (
        <div className="login-container">
          <h3>Loading please wait...</h3>
        </div>
      )
    } else if (state.is_valid_signup_token === false) {
      content = (
        <div className="login-container">
          <div className="main-login">
            <section>
              <div className="main-login-container">
                <div className="error-template">
                  <h3 className="text-center">Link has expired...</h3>
                </div>
              </div>
            </section>
          </div>
        </div>
      )
    } else {
      content = <ActionPageLoader />
    }
    return (
      <div className="login-container">
        <div className="row">
          <div className="col-sm-12 form-group">
            <div className="login-btn">{content}</div>
          </div>
        </div>
      </div>
    )
  })
)

export const Finished = () => {
  return (
    <VisbilityContainer>
      <Panel>
        <H1>Almost there!</H1>
        <CommonPose className="text-center">
          Please check your email to confirm your address and login to your new
          marketplace!
        </CommonPose>
      </Panel>
    </VisbilityContainer>
  )
}

export const PlanAndPaymentInfo = inject('store')(
  observer(({ store }) => {
    const router = useRouter()
    const { siteData } = store
    const plans = siteData.get('plans')
    const [plan, setPlan] = useState(plans[0])
    const [price, setPrice] = useState(plan.prices[0])
    const [coupon, setCoupon] = useState(router.query.dc)
    const changePlan = (plan) => {
      setPrice(plan.prices[0])
      setPlan(plan)
    }

    if (
      typeof window !== 'undefined' &&
      (!plan || !store.sessionData.userData)
    ) {
      router.push(PAGEURL.SIGNUP)
      return
    }

    const onSuccess = () => {
      router.push({
        pathname: PAGEURL.SIGNUP_SUCCESS(1),
        query: { next: router.query.next }
      })
    }

    return (
      <>
        <PageTitle
          title="Chalice Connect - Plan Information"
          description={''}
        />
        <h1>Plan and Payment</h1>
        <div className="row">
          <div className="col-md-6">
            <div className="pricing-table pricing-wide">
              <div style={{ display: 'flex', flex: '1 0 auto' }}>
                <div className="tier-top">
                  <div className="tier-box">
                    <div className="tier-content">
                      <PaymentForm
                        plans={plans}
                        plan={plan}
                        price={price}
                        coupon={coupon}
                        setPlan={changePlan}
                        setPrice={setPrice}
                        setCoupon={setCoupon}
                        onSuccess={onSuccess}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="compare-plans">
              <a href="/pricing/#compare-plans">Compare Plans</a>
            </div>
          </div>
        </div>
      </>
    )
  })
)

// STYLING

const H1 = styled(HeaderPose)`
  font-size: 2.5rem !important;
  padding-bottom: 20px;
`

const Panel = styled.div`
  margin: 0 auto;
  max-width: 800px;
  display: flex;
  flex-direction: column;

  form {
  }

  input {
    border-radius: 10px;
    border: #eee solid 2px;
    padding: 30px 20px;
    font-size: 1.2rem;
  }

  input[type='checkbox'] {
  }
`

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Label = styled.label`
  padding-left: 20px;
  font-size: 1.2rem;
  font-weight: 400;
`

const Action = styled(CommonPose)`
  padding: 50px 0;
`

const Errors = styled.div`
  text-align: center;
  font-weight: bold;
  padding: 10px 0;
  color: ${(props) => props.theme.colors.danger};
  margin: 0 auto;
  font-weight: 400;
`

const FormWrapper = styled.div`
  max-width: ${(props) => (props.maxWidth ? props.maxWidth : '500px')};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 auto;
`

const Row = styled.div`
  margin: 10px 0;
  flex: 0 1 auto;
  display: flex;
  flex-direction: row;
  gap: 20px;

  ${(props) => props.theme.media.phone`
    flex-direction: column;
  `}
`

const Col = styled(CommonPose)`
  flex: 1 1 auto;
`

const RowCenter = styled(Row)`
  text-align: center;
`

const ErrorMessageWrapper = styled.div`
  color: ${(props) => props.theme.colors.danger};
  padding: 10px 0;
  font-size: 0.8rem;
`
